import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { from, Observable, of, shareReplay } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { WindowRefService } from '../gk-dynamic-list/services/window-ref.service';
import {
  PaymentAmountType,
  StampDutyPaymentDto,
} from '../services/stamp-duty/stamp-duty.model';
import { BasicResponse } from '../utils/basic-response/basic-response.model';
import { PortalId } from '../utils/portal-id/portal-id.model';
import { PaymentDetailsModalComponent } from './payment-details-modal/payment-details-modal.component';
import { PaymentDetails } from './payment-details-modal/services/payment-details/payment-details.model';
import {
  FileUuids,
  Invoice,
  Payment,
  PaymentLimitEndpoint,
  PaymentParams,
  PaymentResponse,
  PendingInvoice,
  PendingInvoiceFromApi,
  UnpaidInvoice,
  UnpaidInvoiceFromApi,
} from './payments.model';

@Injectable()
export class PaymentsService {
  private amountLimit: Observable<number>;
  private amountLimitMessage: Observable<string>;
  isPaymentInProgress: EventEmitter<boolean> = new EventEmitter<boolean>();
  isPaymentBeginCalled: EventEmitter<boolean> = new EventEmitter<boolean>();
  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService,
    private toastr: ToastrService,
    private windowRefService: WindowRefService,
    private ngbModal: NgbModal,
  ) {}

  pay(paymentParams: PaymentParams): void {
    this.isPaymentInProgress.emit(true);
    (paymentParams.paymentWithoutCheckingLimit
      ? of(false)
      : this.isPaymentAmountOverLimit(
          paymentParams.invoices,
          paymentParams.pathToAmount,
        )
    )
      .pipe(
        filter((isPaymentAmountOverLimit) => !isPaymentAmountOverLimit),
        switchMap(() =>
          paymentParams.paymentOwnerRequired
            ? this.askForPaymentDetails(
                paymentParams.stampDutyPayment.PortalId,
                paymentParams.paymentAmountType,
              )
            : of(undefined),
        ),
        switchMap((paymentDetails) =>
          this.payOrder(
            paymentParams.stampDutyPayment
              ? undefined
              : this.getInvoicesIds(
                  paymentParams.invoices,
                  paymentParams.invoiceIdFieldName,
                ),
            this.getReturnUrl(paymentParams.paymentEndSimpleInfo),
            paymentParams.multiplePaymentProviders,
            paymentParams.parent,
            paymentParams.openSelectedProviderWindow,
            paymentParams.stampDutyPayment
              ? {
                  ...paymentParams.stampDutyPayment,
                  OwnerId: paymentDetails?.paymentOwner?.id,
                  Ilosc: paymentDetails?.numberOfPayments,
                  Kwota: paymentDetails?.paymentAmount,
                }
              : undefined,
          ),
        ),
      )
      .subscribe({
        next: (res) =>
          this.handlePayOrderSuccessResponse(
            res,
            paymentParams.paymentInNewTab,
            paymentParams.abortPendingStatusOnRedirect,
          ),
        error: (err) => this.handlePayOrderFailResponse(err),
      });
  }

  askForPaymentDetails(
    portalId: PortalId,
    paymentAmountType: PaymentAmountType,
  ): Observable<PaymentDetails> {
    return new Observable((observer) => {
      const modal = this.ngbModal.open(PaymentDetailsModalComponent, {
        backdrop: 'static',
        keyboard: false,
      });
      const componentInstance =
        modal.componentInstance as PaymentDetailsModalComponent;
      componentInstance.portalId = portalId;
      componentInstance.paymentAmountType = paymentAmountType;
      modal.result.then(() => {
        observer.next(componentInstance.getFormValue());
        observer.complete();
      });
    });
  }

  getUnpaidInvoices(): Observable<UnpaidInvoice[]> {
    return this.httpClient
      .get<UnpaidInvoiceFromApi[]>('/api/system/payment/unpaid/invoices')
      .pipe(
        map((res) =>
          res.map((unpaidInvoiceFromApi) =>
            UnpaidInvoice.fromApiToApp(unpaidInvoiceFromApi),
          ),
        ),
      );
  }

  getPendingInvoices(): Observable<PendingInvoice[]> {
    return this.httpClient
      .get<
        PendingInvoiceFromApi[]
      >('/api/system/payment/unpaid/invoices/pending')
      .pipe(
        map((res) =>
          res.map((pendingInvoiceFromApi) =>
            PendingInvoice.fromApiToApp(pendingInvoiceFromApi),
          ),
        ),
      );
  }

  downloadInvoices(fileIds: FileUuids): Observable<HttpResponse<Blob>> {
    return this.httpClient.post('/api/system/edok/download', fileIds, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  cancelPayment(transId: number | string): Observable<number> {
    return this.httpClient.post<number>(
      '/api/payments/webewidpayments/cancel',
      {
        TransId: transId,
      },
    );
  }

  payOrder(
    invoicesId: string[],
    returnUrl: string,
    multiplePaymentProviders = false,
    parent: any,
    openSelectedProviderWindow: (providers: string[]) => Promise<string>,
    stampDutyPayment?: StampDutyPaymentDto,
  ): Observable<PaymentResponse> {
    return this.httpClient
      .get<string[]>('/api/payments/available/methods')
      .pipe(
        switchMap((paymentProviders) => {
          if (multiplePaymentProviders) {
            return from(
              openSelectedProviderWindow.call(parent, paymentProviders),
            );
          } else {
            return of(paymentProviders[0]);
          }
        }),
        switchMap((paymentProvider) => {
          if (!paymentProvider) {
            return of(undefined);
          }
          const url = '/api/payments/webewidpayments/begin';

          return this.httpClient
            .post<PaymentResponse>(url, {
              FaktUuids: invoicesId,
              ReturnUrl: returnUrl,
              PaymentProvider: paymentProvider,
              OplataSkarbowa: stampDutyPayment,
            })
            .pipe(
              tap(() => {
                this.isPaymentBeginCalled.emit(true);
              }),
            );
        }),
      );
  }

  getAmountLimit(): Observable<number> {
    if (!this.amountLimit) {
      this.amountLimit = this.httpClient
        .get<BasicResponse<number>>(PaymentLimitEndpoint.Amount)
        .pipe(
          map((response) => response.Result),
          shareReplay(1),
        );
    }

    return this.amountLimit;
  }

  getAmountLimitMessage(): Observable<string> {
    if (!this.amountLimitMessage) {
      this.amountLimitMessage = this.httpClient
        .get<BasicResponse>(PaymentLimitEndpoint.Message)
        .pipe(
          map((response) => response.Result),
          shareReplay(1),
        );
    }

    return this.amountLimitMessage;
  }

  getInvoicesIds(
    invoices: Invoice[] | Payment[],
    invoiceIdFieldName: string,
  ): string[] {
    return invoices.map((item) => item[invoiceIdFieldName]);
  }

  getReturnUrl(paymentEndSimpleInfo = false): string {
    return paymentEndSimpleInfo
      ? `${window.location.origin}/payment-end-simple-info/payment-end-simple-info`
      : `${this.windowRefService.nativeWindow.location.href.split('?')[0]}`;
  }

  handlePayOrderSuccessResponse(
    res: { RedirectUrl: string },
    paymentInNewTab: boolean,
    abortPendingStatusOnRedirect?: boolean,
  ): void {
    if (!res) {
      this.isPaymentInProgress.emit(false);

      return;
    }
    if (paymentInNewTab) {
      this.windowRefService.nativeWindow
        .open(res.RedirectUrl, '_blank')
        .focus();
      if (abortPendingStatusOnRedirect) {
        this.isPaymentInProgress.emit(false);
      }
    } else {
      this.windowRefService.nativeWindow.location.href = res.RedirectUrl;
    }
  }

  handlePayOrderFailResponse(err: HttpErrorResponse): void {
    this.toastr.error(
      _.get(err, 'error.ResponseStatus.Message', 'ERROR_OCCURRED'),
    );
    console.error(err);
    this.isPaymentInProgress.emit(false);
  }

  maybeShowAmountLimitMessage(
    limitAmount: number,
    invoices: Payment[] | Invoice[],
    pathToAmount: string,
  ): Observable<boolean> {
    const amounts = invoices.map((invoice) =>
      parseFloat(invoice[pathToAmount]),
    );
    const totalAmount = amounts.reduce((sum, amount) => sum + amount);
    if (totalAmount <= limitAmount || !limitAmount) {
      return of(false);
    }
    return this.getAmountLimitMessage().pipe(
      map((message) => {
        if (message) {
          this.setAmountLimitMessageAndStopPaymentProgress(message);
        } else {
          this.setDefaultAmountLimitMessage(limitAmount);
        }

        return true;
      }),
    );
  }

  setDefaultAmountLimitMessage(limitAmount: number): void {
    this.translateService
      .get('GK.PAYMENTS.PAYMENT_OVER_LIMIT')
      .subscribe((value) =>
        this.setAmountLimitMessageAndStopPaymentProgress(
          value.replace('${limit}', limitAmount.toFixed(2)),
        ),
      );
  }

  setAmountLimitMessageAndStopPaymentProgress(message: string): void {
    this.toastr.warning(message);
    this.isPaymentInProgress.emit(false);
  }

  isPaymentAmountOverLimit(
    invoices: Payment[] | Invoice[],
    pathToAmount: string,
  ): Observable<boolean> {
    return this.getAmountLimit().pipe(
      switchMap((limitAmount) =>
        this.maybeShowAmountLimitMessage(limitAmount, invoices, pathToAmount),
      ),
    );
  }
}
